import { useCountries } from 'translation/useCountries';
import { ProviderSetupDTO } from '../../../@types/providers';
import CountryFlag from 'components/icons/CountryFlag';
import { Button, TextField } from '@nps.today/component-library';
import { useEffect } from 'react';
import {
  Card,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Tooltip,
  Box,
  Select
} from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import useCustomDispatch from 'redux/dispatch';
import { RootState, useSelector } from 'redux/store';
import {
  getProviders,
  getCountryCodes,
  getCurrentProviderSetup,
  updateCurrentProviderSetup
} from 'redux/slices/providers';
import { useTranslator } from 'translation/useTranslator';
import { CustomAutocomplete } from 'components/util/CustomAutocomplete';
import AccessGuard, { rules, useAccess } from 'access';

type ProviderForm = {
  currentProviders: ProviderSetupDTO | undefined;
};

const Providers = () => {
  const translator = useTranslator();
  const countries = useCountries();

  const customDispatch = useCustomDispatch();
  const { isLoading, providers, countryCodes, currentProviderSetup } = useSelector(
    (state: RootState) => state.providers
  );

  const { organizationProfile } = useSelector((state: RootState) => state.organization);

  const canManageProviders = useAccess(rules.provider.manage);

  useEffect(() => {
    if (!organizationProfile.id) {
      return;
    }
    customDispatch({
      action: getProviders,
      disableSuccessMessage: true
    });
    customDispatch({
      action: getCountryCodes,
      disableSuccessMessage: true
    });
    customDispatch({
      action: getCurrentProviderSetup,
      actionParameters: {
        orgId: organizationProfile.id
      },
      disableSuccessMessage: true
    });
  }, [customDispatch, organizationProfile]);

  const isFormLoading =
    isLoading.getProviders ?? isLoading.getCountryCodes ?? isLoading.getCurrentProviderSetup;

  const formik = useFormik<ProviderForm>({
    enableReinitialize: true,
    initialValues: {
      currentProviders: currentProviderSetup
    },
    onSubmit: async (values) => {
      customDispatch({
        action: updateCurrentProviderSetup,
        actionParameters: {
          orgId: organizationProfile.id,
          providerSetup: values.currentProviders
        },
        disableSuccessMessage: false
      });
    }
  });

  const { handleSubmit, dirty, setFieldValue, isSubmitting } = formik;

  const formFields = (
    <Grid container spacing={3}>
      <Grid key={'emailProvider'} size={12}>
        <FormControl key="emailProvider" fullWidth>
          <InputLabel id="emailProvider">{translator.emailProvider()}</InputLabel>
          <Select
            value={formik.values.currentProviders?.Email?.providerId}
            onChange={formik.handleChange}
            disabled={!canManageProviders}
            fullWidth
            labelId="emailProvider"
            label={translator.emailProvider()}
            name={'currentProviders.Email.providerId'}
          >
            {providers
              ?.filter((setup) => setup.type === 'Email')
              ?.map((setup) => (
                <MenuItem key={setup.id} value={setup.id}>
                  {setup.displayName}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid key={'smsProvider'} container size={12} spacing={3}>
        <Grid key={'smsProvider'} size={{ xs: 12, md: 6 }}>
          <FormControl key="smsProvider" fullWidth>
            <InputLabel id="smsProvider">{translator.smsProvider()}</InputLabel>
            <Select
              value={formik.values.currentProviders?.SMS?.providerId}
              key="2"
              fullWidth
              disabled={!canManageProviders}
              label={translator.smsProvider()}
              onChange={formik.handleChange}
              name={'currentProviders.SMS.providerId'}
            >
              {providers
                ?.filter((setup) => setup.type === 'SMS')
                ?.map((setup) => (
                  <MenuItem key={setup.id} value={setup.id}>
                    {setup.displayName}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
        <Tooltip title={translator.defaultCountryCodeTooltip()} placement="top" arrow>
          <Grid key={'countryCode'} size={{ xs: 12, md: 6 }}>
            <FormControl key="countryCode" fullWidth>
              <CustomAutocomplete
                dataTestId="default-country-code"
                id="country-select-demo"
                fullWidth
                disabled={!canManageProviders}
                options={countryCodes}
                autoHighlight
                value={
                  countryCodes.find(
                    (option) =>
                      option.country === formik.values.currentProviders?.SMS?.defaultCountryCode
                  ) ?? null
                }
                getOptionLabel={(option) =>
                  `(${option.countryCode}) ${
                    countries.getCountryName(option.country) ?? option.country
                  }`
                }
                filterOptions={(options, { inputValue }) =>
                  options.filter(
                    (option) =>
                      option.countryCode.includes(inputValue) ||
                      (countries.getCountryName(option.country) ?? option.country)
                        .toLowerCase()
                        .includes(inputValue.toLowerCase())
                  )
                }
                onChange={(_, value) => {
                  setFieldValue(
                    'currentProviders.SMS.defaultCountryCode',
                    value?.country ?? undefined
                  );
                }}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return (
                    <Box
                      key={key}
                      component="li"
                      sx={{ '& > img': { flexShrink: 0 } }}
                      gap={1}
                      {...optionProps}
                    >
                      <CountryFlag country={option.country} />({option.countryCode}){' '}
                      {countries.getCountryName(option.country) ?? option.country}
                    </Box>
                  );
                }}
                renderInput={(params) => {
                  const selectedOption = countryCodes.find(
                    (option) =>
                      option.country === formik.values.currentProviders?.SMS?.defaultCountryCode
                  );
                  return (
                    <TextField
                      dataTestId="default-country-code"
                      {...params}
                      label={translator.defaultCountryCode()}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: selectedOption ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                            <CountryFlag country={selectedOption.country} />
                          </Box>
                        ) : null
                      }}
                    />
                  );
                }}
              />
            </FormControl>
          </Grid>
        </Tooltip>
      </Grid>
    </Grid>
  );

  return (
    <Card sx={{ p: 3 }} style={{ maxWidth: '45rem' }}>
      {isFormLoading ? (
        <LinearProgress />
      ) : (
        <FormikProvider value={formik}>
          <Form id="security" noValidate onSubmit={handleSubmit}>
            {formFields}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <AccessGuard rule={rules.provider.manage}>
                <Button
                  dataTestId={`change-provider-save`}
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  disabled={!dirty}
                >
                  {translator.save()}
                </Button>
              </AccessGuard>
            </Box>
          </Form>
        </FormikProvider>
      )}
    </Card>
  );
};

export default Providers;
